import { useChildMatches, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ReviewDetail } from "@/components/review/review-detail";
import { ReviewMiniRail } from "@/components/review/review-mini-rail";
import { ReviewQueue } from "@/components/review/review-queue";
import type { TriageFilters, TriageItemRow } from "@/hooks/use-triage";
import { useTriageItems } from "@/hooks/use-triage";
import { cn } from "@/lib/utils";

const COLLAPSED_KEY = "sp-review-queue-collapsed";

export function ReviewPage() {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const search = useSearch({ from: "/s/$solutionId/review" });
  const navigate = useNavigate();

  const filters: TriageFilters = useMemo(
    () => ({
      status: search.status,
      type: search.type,
      speakerId: search.speakerId,
      search: search.search,
    }),
    [search.status, search.type, search.speakerId, search.search],
  );

  const childMatches = useChildMatches();
  const itemMatch = childMatches.find((m) => m.routeId.endsWith("/$itemId"));
  const selectedItemId =
    itemMatch && "itemId" in itemMatch.params ? (itemMatch.params.itemId as string) : null;

  const queue = useTriageItems(solutionId, filters);
  const items = queue.data?.data ?? [];
  const ids = items.map((i) => i.id);
  const idx = selectedItemId ? ids.indexOf(selectedItemId) : -1;
  const prevId = idx > 0 ? ids[idx - 1] : null;
  const nextId = idx >= 0 && idx < ids.length - 1 ? ids[idx + 1] : null;

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  });

  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Auto-land on the first item when arriving at /review with no selection,
  // so the user starts triaging immediately. Replaces history so the back
  // button still goes to wherever the PM came from.
  const firstId = ids[0];
  useEffect(() => {
    if (!selectedItemId && firstId) {
      navigate({
        to: "/s/$solutionId/review/$itemId",
        params: { solutionId, itemId: firstId },
        search: (prev) => prev,
        replace: true,
      });
    }
  }, [selectedItemId, firstId, solutionId, navigate]);

  function setFilters(next: TriageFilters) {
    navigate({
      to: "/s/$solutionId/review",
      params: { solutionId },
      search: (prev) => ({
        ...prev,
        status: next.status,
        type: next.type,
        speakerId: next.speakerId,
        search: next.search,
      }),
    });
  }

  function setView(view: "row" | "tile") {
    navigate({
      to: "/s/$solutionId/review",
      params: { solutionId },
      search: (prev) => ({ ...prev, view }),
    });
  }

  function selectItem(item: TriageItemRow) {
    navigate({
      to: "/s/$solutionId/review/$itemId",
      params: { solutionId, itemId: item.id },
      search: (prev) => prev,
    });
  }

  function gotoId(id: string | null) {
    if (!id) return;
    navigate({
      to: "/s/$solutionId/review/$itemId",
      params: { solutionId, itemId: id },
      search: (prev) => prev,
    });
  }

  // Keyboard nav: j/k or arrows for prev/next item
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "j" || e.key === "ArrowDown") {
        if (nextId) {
          e.preventDefault();
          gotoId(nextId);
        }
      } else if (e.key === "k" || e.key === "ArrowUp") {
        if (prevId) {
          e.preventDefault();
          gotoId(prevId);
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevId, nextId]);

  return (
    <div
      className={cn(
        "grid h-[calc(100vh-58px)] transition-[grid-template-columns] duration-200",
        collapsed ? "grid-cols-[56px_1fr]" : "grid-cols-[420px_1fr]",
      )}
    >
      {collapsed ? (
        <ReviewMiniRail
          items={items}
          selectedItemId={selectedItemId}
          onSelect={selectItem}
          onPrev={() => gotoId(prevId)}
          onNext={() => gotoId(nextId)}
          onExpand={() => setCollapsed(false)}
        />
      ) : (
        <aside className="border-r border-[var(--color-border)] bg-card overflow-hidden">
          <ReviewQueue
            solutionId={solutionId}
            filters={filters}
            onFilterChange={setFilters}
            selectedItemId={selectedItemId}
            onSelectItem={selectItem}
            onCollapse={() => setCollapsed(true)}
            view={search.view}
            onViewChange={setView}
          />
        </aside>
      )}
      <section className="@container overflow-hidden relative">
        {selectedItemId && (
          <ReviewDetail
            itemId={selectedItemId}
            prevItemId={prevId ?? null}
            nextItemId={nextId ?? null}
            collapsed={collapsed}
          />
        )}
        {!selectedItemId && (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-12 text-center">
            <div>
              <p className="font-display text-base text-foreground/80 mb-1">
                Select an item to triage
              </p>
              <p>Pick from the queue on the left to view details and decide.</p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export function ReviewItemPage() {
  return null;
}
